import WebMercatorViewport from 'viewport-mercator-project';
import { LngLatBounds } from 'mapbox-gl';

import MainMenuStore from '../../../../router/navigation/store/main-nav-container.store';
import { IGeolocationDto } from '../../models/RootStore.base';

import { PanelsProperties } from './getPanelPropertiesAndDimensions';
import {
  MAP_DEFAULT_ZOOM,
  DEFAULT_VIEW_STATE_LOCATION,
} from '../index';
import getOffsetCoordinates from './getOffsetCoordinates';
import getCroppedMapDimensions from './getCroppedMapDimensions';
import getBoundsPadding from './getBoundsPadding';

const computeMapPosition = (
  mapBounds: LngLatBounds | null,
  geolocation: IGeolocationDto | undefined,
  panelPropertiesAndDimensions: PanelsProperties & { left: number; right: number; bottom: number },
  mapWidth: number,
  mapHeight: number,
  viewStateZoom: number | undefined,
  fitExactBounds?: boolean,
): { latitude: number; longitude: number; zoom: number } => {
  let latitude;
  let longitude;
  let zoom;

  const {
    isRightPanelOpen,
    isLeftPanelOpen,
    isBottomPanelOpen,
    currentLeftPanelWidth,
    currentRightPanelWidth,
    currentBottomPanelHeight,
    left,
    right,
    bottom,
  } = panelPropertiesAndDimensions;
  if (mapBounds) {
    const southWest: [number, number] = [mapBounds.getSouthWest().lng, mapBounds.getSouthWest().lat];
    const northEast: [number, number] = [mapBounds.getNorthEast().lng, mapBounds.getNorthEast().lat];

    const horizontalPanelsWidth = left + right;
    const verticalPanelsHeight = bottom;

    // Get the dimensions of the map view that is cropped by the panels
    // Use the dimensions of the large view and then subtract the dimensions of the open panels
    const innerMapDimensions = getCroppedMapDimensions({
      width: mapWidth,
      height: mapHeight,
    }, {
      horizontal: horizontalPanelsWidth + MainMenuStore.width,
      vertical: verticalPanelsHeight,
    });

    // Fit the map bounds in the context of the cropped map view to make sure that the new route can fit in the smaller map view
    const TOP_PADDING = 100;
    const PADDING = 80;

    const innerMapBounds = new WebMercatorViewport(innerMapDimensions)
      .fitBounds([southWest, northEast], {
        padding: getBoundsPadding(innerMapDimensions, {
          top: TOP_PADDING, left: PADDING, right: PADDING, bottom: PADDING,
        }),
      });

    const panelProps = {
      isRightPanelOpen, isLeftPanelOpen, isBottomPanelOpen, currentLeftPanelWidth, currentRightPanelWidth, currentBottomPanelHeight,
    };

    if ((horizontalPanelsWidth + MainMenuStore.width) >= mapWidth || verticalPanelsHeight >= mapHeight) {
      return {
        latitude: innerMapBounds.latitude,
        longitude: innerMapBounds.longitude,
        zoom: innerMapBounds.zoom,
      };
    }

    // Send inner map bounded context to get the offset coordinates so that the bounds are centered in the cropped map view,
    // but ultimately offset in the uncropped map view, based on what panels are open
    const offsetCoordinates = getOffsetCoordinates({
      longitude: innerMapBounds.longitude,
      latitude: innerMapBounds.latitude,
      viewport: innerMapBounds,
      viewStateZoom,
      panelProperties: panelProps,
      fitExactBounds,
    });

    [longitude, latitude, zoom] = offsetCoordinates;
  } else if (geolocation) {
    latitude = geolocation.latitude || 0;
    longitude = geolocation.longitude || 0;
    zoom = MAP_DEFAULT_ZOOM;
  } else {
    latitude = DEFAULT_VIEW_STATE_LOCATION.latitude;
    longitude = DEFAULT_VIEW_STATE_LOCATION.longitude;
    zoom = MAP_DEFAULT_ZOOM;
  }

  return { latitude, longitude, zoom };
};

export default computeMapPosition;
